/**
 * Driver License Verification — photo-first flow.
 *
 * Same UX as identity.tsx: native document scanner → auto-crop → upload
 * → Didit /v3/id-verification/ for license OCR + authenticity → inline
 * editable field card for confirmation. Claude Vision fallback on the
 * edge function side.
 */
import { useCallback, useEffect, useState } from 'react'
import { ActivityIndicator, Linking, View, Text } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { Image as ExpoImage } from 'expo-image'
import { useQueryClient } from '@tanstack/react-query'
import Animated, { FadeInDown, FadeOut, useReducedMotion } from 'react-native-reanimated'
import {
  Camera,
  CheckCircle,
  AlertCircle,
  RotateCcw,
  ShieldCheck,
  Upload,
} from '@/components/ui/icons'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { useIsDesktopWeb } from '@/lib/hooks/use-is-desktop-web'
import { isExpectedNonFatalOperationalError } from '@/lib/errors/service-error'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { Pressable } from '@/components/ui/Pressable'
import { Select, type SelectOption } from '@/components/ui/Select'
import { StructuredTextInput } from '@/components/ui/StructuredTextInput'
import { ExtractedFieldsCard } from '@/components/verification/ExtractedFieldsCard'
import { useLicenseVerification } from '@/lib/hooks/use-license-verification'
import { useVerificationOverview, verificationKeys } from '@/lib/hooks/use-verification'
import { useAuth } from '@/lib/hooks/use-auth'
import { verificationService } from '@/lib/services/verification.service'
import { identityService } from '@/lib/services/identity.service'
import { todayISO } from '@/lib/utils/dates'
import { compactSvDui, SV_DUI_DIGIT_LENGTH } from '@/lib/utils/sv-identity'
import { useSemanticColors, SPACING, RADIUS, DURATION } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { COLORS, FONTS } from '@/lib/brand'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { toast } from '@/lib/toast'
import { captureError } from '@/lib/utils/observability'
import type { LicenseClass } from '@/lib/types/verification.types'
import { findCountry } from '@/lib/data/countries'

// México incluida (16/10): licencias mexicanas válidas para conducir se
// verifican por OCR de Didit — sin Sertracen, que es solo salvadoreño.
const LICENSE_COUNTRIES = ['SV', 'GT', 'HN', 'NI', 'CR', 'PA', 'BZ', 'MX'] as const

type SertracenPrefix = 'SS' | 'LD' | 'PM' | 'SM' | 'SA' | 'CS'

const SERTRACEN_PREFIX_OPTIONS: SelectOption<SertracenPrefix>[] = [
  { value: 'SS', label: 'SS' },
  { value: 'LD', label: 'LD' },
  { value: 'PM', label: 'PM' },
  { value: 'SM', label: 'SM' },
  { value: 'SA', label: 'SA' },
  { value: 'CS', label: 'CS' },
]

export default function LicenseScreen() {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const isDesktopWeb = useIsDesktopWeb()

  const { data: overview, isLoading: overviewLoading } = useVerificationOverview()
  const licenseApproved = overview?.license.status === 'approved'

  const [issuingCountry, setIssuingCountry] = useState('SV')
  const flow = useLicenseVerification({
    userId: user?.id ?? null,
    issuingCountry,
  })

  const {
    state,
    captureFront,
    captureBack,
    retakeFront,
    retakeBack,
    updateField,
    applyResult,
    reset,
  } = flow
  const [persisting, setPersisting] = useState(false)

  // Photo-first license_class confirmation. The OCR
  // pipeline doesn't extract license class reliably (small text, varies
  // by country), so the service seeds 'liviana' on the initial upsert as
  // the most-common SV default. Without this Select, the user has no
  // way to override and the wrong class would silently persist for
  // motorcycle / heavy-vehicle / special-license drivers.
  const [licenseClass, setLicenseClass] = useState<LicenseClass>('liviana')
  const [controlPrefix, setControlPrefix] = useState<SertracenPrefix>('CS')
  const [controlNumber, setControlNumber] = useState('')
  const waitingForPersistedLicense = overviewLoading && state.phase === 'idle'

  useEffect(() => {
    const extractedPrefix = normalizeSertracenPrefix(state.extracted?.controlPrefix)
    const extractedControlNumber = state.extracted?.controlNumber?.replace(/\D/g, '') ?? ''
    if (extractedPrefix) setControlPrefix(extractedPrefix)
    if (!controlNumber && extractedControlNumber) setControlNumber(extractedControlNumber)
  }, [controlNumber, state.extracted?.controlNumber, state.extracted?.controlPrefix])

  useEffect(() => {
    if (state.phase === 'confirmed') {
      const timer = setTimeout(() => router.back(), 1200)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [state.phase, router])

  const handleConfirm = useCallback(async () => {
    // Expiry parity with license-manual.tsx:90-104. The OCR
    // edge fn persists whatever expiry it reads, including past dates,
    // and Didit may still return 'approved' status if the doc looks
    // structurally valid. Reject expired licenses client-side before
    // the user finalizes — same UX guarantee the manual flow gives.
    // The user can edit the expiry via ExtractedFieldsCard above before
    // re-tapping Confirm.
    const expiry = state.extracted?.expiryDate
    if (expiry && expiry <= todayISO()) {
      toast.error(t('errors.document_expired'))
      return
    }

    setPersisting(true)
    try {
      const driverLicenseId = state.recordId
      const licenseNumber = state.extracted?.documentNumber?.trim() ?? null
      const cleanControlNumber = controlNumber.replace(/\D/g, '')

      if (!user?.id || !driverLicenseId) {
        toast.error(t('verification.error_failed'))
        return
      }

      if (issuingCountry !== 'SV') {
        const result = await verificationService.confirmRegionalDriverLicense(user.id, {
          driverLicenseId,
          licenseNumber,
          licenseClass,
          issueDate: state.extracted?.issueDate ?? null,
          expiryDate: state.extracted?.expiryDate ?? null,
        })
        applyResult(result)
        await queryClient.invalidateQueries({ queryKey: verificationKeys.overview(user.id) })
        return
      }

      // This used to be an inline supabase.from('identity_documents') query
      // right here, inside the component, because nothing in the service layer
      // could answer "what identity has this person proven?". That gap is also
      // why the payout form asked a host to retype a DUI the registry had
      // already confirmed. One reader now, and it judges "proven" by the
      // status columns rather than by verified_at — a superseded duplicate
      // keeps its timestamp.
      const verifiedIdentity = await identityService.getVerifiedIdentity(user.id)
      const duiNumber = compactSvDui(verifiedIdentity?.documentNumber ?? '')
      if (duiNumber.length !== SV_DUI_DIGIT_LENGTH) {
        const result = await verificationService.confirmDriverLicenseForManualReview(user.id, {
          driverLicenseId,
          licenseNumber,
          licenseClass,
          issueDate: state.extracted?.issueDate ?? null,
          expiryDate: state.extracted?.expiryDate ?? null,
          extracted: state.extracted ?? {},
          rejectionReason: t('verification.sertracen_identity_pending'),
        })
        applyResult(result)
        const overviewKey = verificationKeys.overview(user.id)
        await queryClient.invalidateQueries({ queryKey: overviewKey })
        await queryClient.refetchQueries({ queryKey: overviewKey, type: 'active' })
        toast.info(t('verification.license_manual_review_title'), {
          description: t('verification.license_manual_review_body'),
        })
        return
      }

      if (!cleanControlNumber) {
        const result = await verificationService.confirmDriverLicenseForManualReview(user.id, {
          driverLicenseId,
          licenseNumber,
          licenseClass,
          issueDate: state.extracted?.issueDate ?? null,
          expiryDate: state.extracted?.expiryDate ?? null,
          extracted: state.extracted ?? {},
          rejectionReason: t('verification.sertracen_missing_fields'),
        })
        applyResult(result)
        const overviewKey = verificationKeys.overview(user.id)
        await queryClient.invalidateQueries({ queryKey: overviewKey })
        await queryClient.refetchQueries({ queryKey: overviewKey, type: 'active' })
        toast.info(t('verification.license_manual_review_title'), {
          description: t('verification.license_manual_review_body'),
        })
        return
      }

      const result = await verificationService.verifyDriverLicenseWithSertracen(user.id, {
        driverLicenseId,
        licenseNumber,
        duiNumber,
        controlPrefix,
        controlNumber: cleanControlNumber,
        licenseClass,
        issueDate: state.extracted?.issueDate ?? null,
        expiryDate: state.extracted?.expiryDate ?? null,
      })
      applyResult(result)
      const overviewKey = verificationKeys.overview(user.id)
      await queryClient.invalidateQueries({ queryKey: overviewKey })
      await queryClient.refetchQueries({ queryKey: overviewKey, type: 'active' })

      if (result.status === 'approved') {
        toast.success(t('verification.result_approved_title'))
      } else {
        toast.info(t('verification.license_manual_review_title'), {
          description: t('verification.license_manual_review_body'),
        })
      }
    } catch (err) {
      // Igual que en identidad: un rechazo de entrada (4xx → VALIDATION_ERROR)
      // es un resultado esperado que la pantalla ya sabe explicar; solo las
      // fallas de verdad —proveedor caído, 5xx, red— llegan a Sentry.
      if (!isExpectedNonFatalOperationalError(err)) {
        captureError(err, { screen: 'license', action: 'sertracen-license-verification' })
      }
      if (user?.id && state.recordId) {
        try {
          const result = await verificationService.confirmDriverLicenseForManualReview(user.id, {
            driverLicenseId: state.recordId,
            licenseNumber: state.extracted?.documentNumber?.trim() ?? null,
            licenseClass,
            issueDate: state.extracted?.issueDate ?? null,
            expiryDate: state.extracted?.expiryDate ?? null,
            extracted: state.extracted ?? {},
            rejectionReason: t(
              issuingCountry === 'SV'
                ? 'verification.sertracen_unavailable'
                : 'verification.license_manual_review_body',
            ),
          })
          applyResult(result)
          const overviewKey = verificationKeys.overview(user.id)
          await queryClient.invalidateQueries({ queryKey: overviewKey })
          await queryClient.refetchQueries({ queryKey: overviewKey, type: 'active' })
          toast.info(t('verification.license_manual_review_title'), {
            description: t('verification.license_manual_review_body'),
          })
          return
        } catch (fallbackErr) {
          captureError(fallbackErr, { screen: 'license', action: 'manual-review-fallback' })
        }
      }
      // Reaching here means BOTH the Sertracen call and the manual-review
      // fallback failed: nothing was stored. The old copy here was
      // `sertracen_unavailable` ("Recibimos tu licencia…"), i.e. a red error
      // toast telling the user we had received a licence we had just failed
      // to save. That string stays as the stored rejection reason above,
      // where it is true.
      toast.error(t('verification.license_save_failed'))
    } finally {
      setPersisting(false)
    }
  }, [
    state.recordId,
    state.extracted,
    controlNumber,
    issuingCountry,
    user?.id,
    t,
    controlPrefix,
    licenseClass,
    applyResult,
    queryClient,
  ])

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader title={t('verification.license_title')} backTestID="license.back" />

      {/* `flexGrow: 1` only guarantees the content container spans the
          viewport; nothing inside claims the leftover height. The capture
          frame is sized by the card it frames (see CapturePrompt) and sits
          directly under the instruction that describes it. */}
      <KeyboardAwareView
        includeTopInset={false}
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: SPACING.lg,
          paddingBottom: SPACING.xl * 2,
          gap: SPACING.lg,
        }}
      >
        {waitingForPersistedLicense ? (
          <LoadingVerificationState />
        ) : licenseApproved ? (
          <ApprovedLicenseState onDone={() => router.back()} />
        ) : (
          <>
            {state.phase === 'idle' && (
              <Select<string>
                label={t('verification.issuing_country')}
                value={issuingCountry}
                onChange={setIssuingCountry}
                options={LICENSE_COUNTRIES.map((iso2) => ({
                  value: iso2,
                  label:
                    findCountry(iso2)?.[i18n.language.startsWith('es') ? 'nameEs' : 'nameEn'] ??
                    iso2,
                }))}
                testID="license.issuing-country"
              />
            )}
            <Text
              style={{
                fontSize: 14,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
                lineHeight: 20,
              }}
            >
              {isDesktopWeb
                ? t('verification.license_upload_intro')
                : t('verification.license_photo_first_intro')}
            </Text>
            <View
              testID="license.not-tarjeta-hint"
              style={{
                padding: SPACING.md,
                borderRadius: RADIUS.lg,
                borderWidth: 1,
                borderColor: `${COLORS.coral.DEFAULT}33`,
                backgroundColor: isDark ? 'rgba(255,107,90,0.10)' : 'rgba(255,107,90,0.06)',
              }}
            >
              <Text
                style={{
                  fontSize: 13,
                  lineHeight: 18,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                }}
              >
                {t('verification.license_not_tarjeta_hint')}
              </Text>
            </View>

            {(state.phase === 'idle' || state.phase === 'scanning-front') && (
              <CapturePrompt
                title={
                  isDesktopWeb
                    ? t('verification.license_upload_instruction_front')
                    : t('verification.license_instruction_front')
                }
                cta={isDesktopWeb ? t('verification.upload_front') : t('verification.tap_to_start')}
                icon={isDesktopWeb ? 'upload' : 'camera'}
                onPress={captureFront}
                testID="license.capture-front"
              />
            )}

            {state.phase === 'scanning-back' && state.frontUri ? (
              <>
                <CapturedPhotoRow
                  uri={state.frontUri}
                  label={t('verification.front_captured')}
                  onRetake={retakeFront}
                />
                <CapturePrompt
                  title={
                    isDesktopWeb
                      ? t('verification.license_upload_instruction_back')
                      : t('verification.license_instruction_back')
                  }
                  cta={
                    isDesktopWeb
                      ? t('verification.upload_back')
                      : t('verification.tap_to_capture_back')
                  }
                  icon={isDesktopWeb ? 'upload' : 'camera'}
                  onPress={captureBack}
                  testID="license.capture-back"
                />
              </>
            ) : null}

            {(state.phase === 'uploading' || state.phase === 'verifying') && (
              <VerifyingState
                frontUri={state.frontUri}
                backUri={state.backUri}
                label={t('verification.verifying_generic')}
              />
            )}

            {(state.phase === 'extracted' || state.phase === 'confirmed') && (
              <>
                <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
                  {state.frontUri ? (
                    <ThumbPreview uri={state.frontUri} onRetake={retakeFront} />
                  ) : null}
                  {state.backUri ? (
                    <ThumbPreview uri={state.backUri} onRetake={retakeBack} />
                  ) : null}
                </View>
                <ExtractedFieldsCard
                  data={state.extracted}
                  onChange={updateField}
                  fields={['documentNumber', 'issueDate', 'expiryDate']}
                />
                {/* Explicit license-class confirmation.
                OCR doesn't extract this field reliably; we previously
                silently defaulted to 'liviana' which was wrong for
                ~10% of users (motorcycles, heavy vehicles, special).
                User must review/select before Confirm. */}
                <View>
                  <Select<LicenseClass>
                    label={t('verification.confirm_license_class')}
                    value={licenseClass}
                    onChange={setLicenseClass}
                    options={
                      [
                        { value: 'liviana', label: t('verification.license_class_liviana') },
                        { value: 'pesada', label: t('verification.license_class_pesada') },
                        {
                          value: 'motocicleta',
                          label: t('verification.license_class_motocicleta'),
                        },
                        { value: 'especial', label: t('verification.license_class_especial') },
                      ] satisfies SelectOption<LicenseClass>[]
                    }
                    placeholder={t('verification.license_class')}
                    sheetTitle={t('verification.confirm_license_class')}
                  />
                </View>
                {issuingCountry === 'SV' && (
                  <OfficialSertracenCard
                    controlPrefix={controlPrefix}
                    controlNumber={controlNumber}
                    onControlPrefixChange={setControlPrefix}
                    onControlNumberChange={setControlNumber}
                  />
                )}
                <Button
                  title={t('verification.confirm_looks_good')}
                  onPress={handleConfirm}
                  variant="primary"
                  size="lg"
                  disabled={persisting || state.phase === 'confirmed'}
                  loading={persisting}
                  testID="license.confirm-button"
                />
                {state.phase === 'confirmed' && (
                  <ApprovedBadge
                    desc={
                      state.canonicalStatus === 'approved'
                        ? t('verification.result_approved_desc')
                        : t('verification.license_manual_review_body')
                    }
                  />
                )}
              </>
            )}

            {state.phase === 'declined' && (
              <DeclinedState
                title={t('verification.result_declined_title')}
                desc={state.error ?? t('verification.result_declined_desc')}
                onRetry={reset}
              />
            )}

            {state.phase === 'error' && (
              <DeclinedState
                title={t('verification.error_failed')}
                desc={state.error ?? ''}
                onRetry={reset}
                onOpenSettings={
                  state.errorNeedsSettings ? () => void Linking.openSettings() : undefined
                }
              />
            )}
          </>
        )}
      </KeyboardAwareView>
    </ScreenContainer>
  )
}

// ─── Sub-components (mirror of identity.tsx — consistency between steps) ───

function LoadingVerificationState() {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <View
      testID="license.loading-state"
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: SPACING.xl,
      }}
    >
      {/* `textBrand`, not the raw brand purple — the treatment identity.tsx
          already settled on: #480C82 on the dark page is ~1.5:1, so the only
          thing on screen while the record loads was an invisible spinner. */}
      <ActivityIndicator color={colors.textBrand} accessibilityLabel={t('common.loading')} />
    </View>
  )
}

function ApprovedLicenseState({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()

  return (
    <Animated.View
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
      collapsable={false}
      testID="license.approved-state"
      style={{ gap: SPACING.lg }}
    >
      <View
        style={{
          backgroundColor: colors.surface,
          borderRadius: RADIUS.xl,
          padding: SPACING.xl,
          alignItems: 'center',
          gap: SPACING.md,
          borderWidth: 1,
          borderColor: `${colors.success}33`,
          ...WEB_SHADOWS.md,
        }}
      >
        <View
          style={{
            width: 72,
            height: 72,
            borderRadius: RADIUS.full,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${colors.success}14`,
          }}
        >
          <ShieldCheck size={34} color={colors.success} strokeWidth={1.8} />
        </View>

        <View style={{ gap: SPACING.sm, alignItems: 'center' }}>
          <Text
            style={{
              fontSize: 22,
              lineHeight: 28,
              fontFamily: FONTS.heading,
              color: colors.textPrimary,
              textAlign: 'center',
            }}
          >
            {t('verification.license_approved_title')}
          </Text>
          <Text
            style={{
              fontSize: 15,
              lineHeight: 22,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              textAlign: 'center',
            }}
          >
            {t('verification.license_approved_body')}
          </Text>
        </View>

        <ApprovedBadge desc={t('verification.result_approved_desc')} />
      </View>

      <Button
        title={t('verification.license_approved_cta')}
        onPress={onDone}
        variant="primary"
        size="lg"
        testID="license.approved-done"
      />
    </Animated.View>
  )
}

interface CapturePromptProps {
  title: string
  cta: string
  onPress: () => void
  testID?: string
  icon?: 'camera' | 'upload'
}

/**
 * A Salvadoran licence is an ID-1 card (85.6 × 53.98 mm ≈ 1.586:1 landscape),
 * so the frame is one too: full measure of the copy above it, height derived
 * from the ratio — 342 × 216pt on a phone, the smallest box that still frames
 * the card it asks for.
 *
 * It is sized by that card and by nothing else. Two earlier attempts sized it
 * from the height the ScrollView had left over instead: `flex: 1` alone made a
 * 342 × 580pt portrait slab no licence ever fills, and `flex: 1` + centring
 * made a correct card marooned in ~200pt of empty background above it and
 * ~245pt below — balanced, and unmistakably unfinished. The frame now sits
 * directly under the instruction that points at it, and the surplus collects
 * at the bottom, where the captured-photo row, the fields card and the confirm
 * button land the moment a photo exists.
 */
const ID_CARD_ASPECT = 1.586

function CapturePrompt({ title, cta, onPress, testID, icon = 'camera' }: CapturePromptProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  const Icon = icon === 'upload' ? Upload : Camera
  return (
    <Animated.View
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
      collapsable={false}
      style={{ gap: SPACING.md }}
    >
      {/* Left, like the subtitle above it and the frame below it. Centred, the
          instruction orphaned its last word under itself and made a third
          alignment out of three stacked elements. */}
      <Text
        style={{
          fontSize: 16,
          lineHeight: 22,
          fontFamily: FONTS.body,
          color: colors.textPrimary,
        }}
      >
        {title}
      </Text>
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={cta}
        testID={testID}
        style={{
          // `surfaceSubtle` in dark, not transparent: with no background fill
          // the interior was the page itself, so the frame read as a stray
          // outline enclosing nothing. It is still a lift rather than
          // the solid violet `surface` panel, which read as a card that
          // already holds something instead of an empty slot.
          backgroundColor: isDark ? colors.surfaceSubtle : colors.surface,
          borderWidth: 2,
          borderStyle: 'dashed',
          // Same token the label inside the frame uses. The raw brand purple
          // never adapted: it stayed #480C82 in dark, ~1.5:1 on the page
          // background, while its own caption glowed lavender inside it.
          borderColor: colors.textBrand,
          borderRadius: RADIUS.lg,
          width: '100%',
          aspectRatio: ID_CARD_ASPECT,
          alignItems: 'center',
          justifyContent: 'center',
          gap: SPACING.sm,
        }}
      >
        <Icon size={48} color={colors.textBrand} strokeWidth={1.5} />
        <Text style={{ fontSize: 16, fontFamily: FONTS.bodyBold, color: colors.textBrand }}>
          {cta}
        </Text>
      </Pressable>
    </Animated.View>
  )
}

function OfficialSertracenCard({
  controlPrefix,
  controlNumber,
  onControlPrefixChange,
  onControlNumberChange,
}: {
  controlPrefix: SertracenPrefix
  controlNumber: string
  onControlPrefixChange: (value: SertracenPrefix) => void
  onControlNumberChange: (value: string) => void
}) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <View
      testID="license.sertracen-card"
      style={{
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        padding: SPACING.md,
        gap: SPACING.md,
        borderWidth: 1,
        borderColor: `${COLORS.coral.DEFAULT}55`,
        ...WEB_SHADOWS.md,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.sm }}>
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: RADIUS.md,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${COLORS.coral.DEFAULT}14`,
          }}
        >
          <ShieldCheck size={20} color={COLORS.coral.DEFAULT} strokeWidth={2} />
        </View>
        <View style={{ flex: 1, gap: SPACING.xs }}>
          <Text style={{ fontSize: 16, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
            {t('verification.sertracen_title')}
          </Text>
          <Text
            style={{
              fontSize: 13,
              lineHeight: 20,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
          >
            {t('verification.sertracen_desc')}
          </Text>
        </View>
      </View>

      <Select<SertracenPrefix>
        label={t('verification.sertracen_prefix')}
        value={controlPrefix}
        onChange={onControlPrefixChange}
        options={SERTRACEN_PREFIX_OPTIONS}
        sheetTitle={t('verification.sertracen_prefix')}
        testID="license.sertracen-prefix"
      />

      {/* "Ej:", not a bare "003354569". Nine grey digits in an empty field
          read as a value the screen had already filled in for you — the same
          thing the org-setup NIT placeholder was doing. The prefix is what
          marks it as an example, so it is translatable copy and goes through
          t() like the rest of the app's example placeholders. */}
      <StructuredTextInput
        label={t('verification.sertracen_control_number')}
        value={controlNumber}
        onChangeText={(value) => onControlNumberChange(value.replace(/\D/g, ''))}
        placeholder={t('verification.sertracen_control_number_placeholder')}
        keyboardType="number-pad"
        testID="license.sertracen-control-number"
      />
    </View>
  )
}

function normalizeSertracenPrefix(value: string | null | undefined): SertracenPrefix | null {
  const prefix = value?.trim().toUpperCase()
  return prefix && SERTRACEN_PREFIX_OPTIONS.some((option) => option.value === prefix)
    ? (prefix as SertracenPrefix)
    : null
}

interface CapturedPhotoRowProps {
  uri: string
  label: string
  onRetake: () => void
}

function CapturedPhotoRow({ uri, label, onRetake }: CapturedPhotoRowProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  const { t } = useTranslation()
  return (
    <Animated.View
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
      collapsable={false}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.md,
        backgroundColor: colors.surface,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        ...WEB_SHADOWS.sm,
      }}
    >
      <ExpoImage
        source={{ uri }}
        style={{ width: 56, height: 40, borderRadius: RADIUS.sm }}
        contentFit="cover"
      />
      <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
        <CheckCircle size={16} color={colors.success} />
        <Text style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textPrimary }}>
          {label}
        </Text>
      </View>
      <Pressable
        onPress={onRetake}
        accessibilityRole="button"
        accessibilityLabel={t('verification.retake_photo')}
        hitSlop={8}
        style={{ padding: SPACING.sm }}
      >
        <RotateCcw size={18} color={colors.textSecondary} />
      </Pressable>
    </Animated.View>
  )
}

const THUMB_STYLE = { width: 80, height: 56, borderRadius: RADIUS.sm, overflow: 'hidden' } as const

/**
 * `onRetake` is optional: mid-verification the photos are already uploaded and
 * cannot be retaken. Wiring a no-op in made a button that announced
 * "Volver a tomar" and then did nothing, so the non-interactive case renders
 * as a plain image instead.
 */
function ThumbPreview({ uri, onRetake }: { uri: string; onRetake?: () => void }) {
  const { t } = useTranslation()

  if (!onRetake) {
    return (
      <View style={THUMB_STYLE}>
        <ExpoImage source={{ uri }} style={{ width: '100%', height: '100%' }} contentFit="cover" />
      </View>
    )
  }

  return (
    <Pressable
      onPress={onRetake}
      accessibilityRole="button"
      accessibilityLabel={t('verification.retake_photo')}
      hitSlop={4}
      style={THUMB_STYLE}
    >
      <ExpoImage source={{ uri }} style={{ width: '100%', height: '100%' }} contentFit="cover" />
    </Pressable>
  )
}

interface VerifyingStateProps {
  frontUri: string | null
  backUri: string | null
  label: string
}

function VerifyingState({ frontUri, backUri, label }: VerifyingStateProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  return (
    <Animated.View
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
      collapsable={false}
      style={{ gap: SPACING.md, alignItems: 'center', paddingVertical: SPACING.xl }}
    >
      <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
        {frontUri ? <ThumbPreview uri={frontUri} /> : null}
        {backUri ? <ThumbPreview uri={backUri} /> : null}
      </View>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: SPACING.sm,
          // `textBrand`, like identity.tsx's verifying pill: a 14%-alpha
          // #480C82 wash sits ~1.2:1 on the dark page, so the pill vanished
          // under a caption that was already lavender.
          backgroundColor: `${colors.textBrand}14`,
          paddingHorizontal: SPACING.md,
          paddingVertical: SPACING.sm,
          borderRadius: RADIUS.full,
        }}
      >
        <Text style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textBrand }}>
          {label}
        </Text>
      </View>
    </Animated.View>
  )
}

function ApprovedBadge({ desc }: { desc: string }) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  return (
    <Animated.View
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
      exiting={reducedMotion ? undefined : FadeOut.duration(DURATION.fast)}
      collapsable={false}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.md,
        backgroundColor: `${colors.success}14`,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
      }}
    >
      <CheckCircle size={24} color={colors.success} />
      <Text style={{ flex: 1, fontSize: 14, fontFamily: FONTS.body, color: colors.success }}>
        {desc}
      </Text>
    </Animated.View>
  )
}

interface DeclinedStateProps {
  title: string
  desc: string
  onRetry: () => void
  /**
   * Solo cuando el fallo es un permiso de cámara bloqueado: reintentar no
   * cambia la decisión del sistema, abrir Ajustes sí.
   */
  onOpenSettings?: () => void
}

function DeclinedState({ title, desc, onRetry, onOpenSettings }: DeclinedStateProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { t } = useTranslation()
  const reducedMotion = useReducedMotion()
  return (
    <Animated.View
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
      collapsable={false}
      style={{ gap: SPACING.md }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: SPACING.md,
          backgroundColor: `${colors.error}14`,
          padding: SPACING.md,
          borderRadius: RADIUS.lg,
        }}
      >
        <AlertCircle size={22} color={colors.textError} />
        <View style={{ flex: 1, gap: SPACING.xs }}>
          <Text style={{ fontSize: 15, fontFamily: FONTS.bodyBold, color: colors.textError }}>
            {title}
          </Text>
          {desc ? (
            <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textSecondary }}>
              {desc}
            </Text>
          ) : null}
        </View>
      </View>
      <Button title={t('verification.try_again')} onPress={onRetry} variant="primary" size="lg" />
      {onOpenSettings ? (
        <Button
          title={t('verification.open_settings')}
          onPress={onOpenSettings}
          variant="secondary"
          size="lg"
          testID="verification.open-settings-button"
        />
      ) : null}
    </Animated.View>
  )
}
